<script setup lang="ts">
import { ArrowRight, Check, ChevronDown, Phone, Quote, ShieldCheck, Sparkles, Star } from "lucide-vue-next";
import { onBeforeUnmount, onMounted } from "vue";
import "@/assets/styles/home.css";

const services = [
  { slug: "dachsanierung", number: "01", title: "Dachsanierung", text: "Substanz bewahren, Energie sparen und den Wert Ihres Hauses langfristig sichern." },
  { slug: "flachdacharbeiten", number: "02", title: "Flachdach", text: "Durchdachte Abdichtung, sichere Entwässerung und langlebige Details." },
  { slug: "spenglerarbeiten", number: "03", title: "Spenglerei", text: "Präzise Metallarbeiten für Anschlüsse, Rinnen und saubere Abschlüsse." }
];

const reviews = [
  { name: "Familie Schneider", place: "Mannheim", text: "Von der ersten Beratung bis zur Abnahme hatten wir jederzeit ein gutes Gefühl. Präzise, sauber und zuverlässig." },
  { name: "Michael Bauer", place: "Heidelberg", text: "Man merkt sofort, dass hier echte Fachleute arbeiten. Besonders die Kommunikation war angenehm klar." },
  { name: "Sandra Keller", place: "Ludwigshafen", text: "Nach dem Sturmschaden war das Team schnell vor Ort. Unser Dach wurde sicher und ordentlich instand gesetzt." }
];

let observer: IntersectionObserver | null = null;
onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-visible");
        observer?.unobserve(entry.target);
      }
    });
  }, { threshold: 0.14, rootMargin: "0px 0px -40px" });
  document.querySelectorAll(".reveal").forEach((element) => observer?.observe(element));
});
onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
  <main class="home-new">
    <section class="home-hero">
      <img class="hero-bg" src="/images/home/hero-v2.png" alt="Modernes Steildach bei Sonnenaufgang" />
      <div class="hero-grain" />
      <div class="home-shell hero-layout">
        <div class="hero-copy">
          <span class="eyebrow hero-enter">Meisterbetrieb · Mannheim & Region</span>
          <h1 class="hero-enter delay-1">Dächer, die<br><em>Ruhe geben.</em></h1>
          <p class="hero-enter delay-2">Wir verbinden ehrliches Handwerk mit klarer Planung – für Dächer, die heute überzeugen und morgen noch schützen.</p>
          <div class="hero-actions hero-enter delay-3">
            <RouterLink to="/kontakt" class="home-button accent"><Phone /> Projekt besprechen</RouterLink>
            <RouterLink to="/leistungen" class="home-button ghost">Leistungen entdecken <ArrowRight /></RouterLink>
          </div>
        </div>
        <div class="hero-note hero-enter delay-3">
          <ShieldCheck />
          <div><strong>Persönlich. Verlässlich.</strong><span>Ein Ansprechpartner von der Idee bis zur Abnahme.</span></div>
        </div>
      </div>
      <a href="#handwerk" class="scroll-cue" aria-label="Weiter zum Inhalt"><span>Entdecken</span><ChevronDown /></a>
    </section>

    <section class="trust-bar">
      <div class="home-shell trust-grid reveal">
        <div><strong>15+</strong><span>Jahre Erfahrung</span></div>
        <div><strong>420</strong><span>realisierte Dächer</span></div>
        <div><strong>4,9</strong><span>Kundenbewertung</span></div>
        <div><strong>100%</strong><span>Meisterqualität</span></div>
      </div>
    </section>

    <section id="handwerk" class="craft-section">
      <div class="home-shell craft-grid">
        <div class="craft-visual reveal">
          <div class="image-frame"><img src="/images/home/handwerk-v2.png" alt="Dachdecker prüft eine präzise Metallverkleidung" /></div>
          <div class="image-caption"><span>01</span><p>Das gute Detail entscheidet über Jahrzehnte.</p></div>
        </div>
        <div class="craft-copy reveal reveal-delay">
          <span class="eyebrow dark">Unser Anspruch</span>
          <h2>Man sieht nicht jedes Detail. Aber man spürt, wenn alles stimmt.</h2>
          <p>Ein gutes Dach beginnt mit Zuhören. Wir prüfen genau, erklären verständlich und empfehlen nur, was technisch und wirtschaftlich wirklich sinnvoll ist.</p>
          <ul>
            <li><Check />Saubere Bestandsaufnahme vor Ort</li>
            <li><Check />Verständliche Angebote ohne Überraschungen</li>
            <li><Check />Fachgerechte Ausführung bis ins Detail</li>
          </ul>
          <RouterLink to="/ueber" class="text-link">Unseren Betrieb kennenlernen <ArrowRight /></RouterLink>
        </div>
      </div>
    </section>

    <section class="home-services">
      <div class="home-shell">
        <div class="section-intro reveal"><div><span class="eyebrow dark">Leistungen</span><h2>Kompetenz für jedes Dach.</h2></div><p>Von der ersten Prüfung bis zum letzten Anschluss denken wir Ihr Projekt als Ganzes.</p></div>
        <div class="service-showcase">
          <RouterLink v-for="(service, index) in services" :key="service.slug" :to="`/leistungen/${service.slug}`" class="home-service-card reveal" :style="{ '--delay': `${index * 110}ms` }">
            <img :src="`/images/services/${service.slug}.png`" :alt="service.title" loading="lazy" />
            <div class="service-shade" />
            <span class="service-number">{{ service.number }}</span>
            <div class="service-info"><h3>{{ service.title }}</h3><p>{{ service.text }}</p><span class="round-arrow"><ArrowRight /></span></div>
          </RouterLink>
        </div>
        <RouterLink to="/leistungen" class="all-services reveal">Alle Leistungen ansehen <ArrowRight /></RouterLink>
      </div>
    </section>

    <section class="promise-section">
      <div class="home-shell promise-grid">
        <div class="promise-copy reveal">
          <span class="eyebrow light">So arbeiten wir</span>
          <h2>Klarheit in jeder Phase.</h2>
          <p>Keine unnötige Komplexität. Sie wissen jederzeit, was als Nächstes passiert und wer für Sie da ist.</p>
        </div>
        <ol class="steps-list">
          <li class="reveal"><span>01</span><div><h3>Zuhören & prüfen</h3><p>Wir verstehen Ihr Vorhaben und sehen uns die Substanz genau an.</p></div></li>
          <li class="reveal"><span>02</span><div><h3>Planen & erklären</h3><p>Sie erhalten eine klare Empfehlung und ein transparentes Angebot.</p></div></li>
          <li class="reveal"><span>03</span><div><h3>Umsetzen & übergeben</h3><p>Wir arbeiten koordiniert, sauber und dokumentieren das Ergebnis.</p></div></li>
        </ol>
      </div>
    </section>

    <section class="team-section">
      <div class="home-shell team-split reveal">
        <div class="team-card">
          <img src="/images/home/team-v2.png" alt="Dachdecker-Team bespricht gemeinsam die Baupläne" loading="lazy" />
          <div class="team-overlay" />
          <div class="team-copy"><span class="eyebrow light">Menschen, die mitdenken</span><h2>Ein Team.<br>Ein Wort.</h2><p>Erfahrene Handwerker, klare Absprachen und Respekt vor Ihrem Zuhause.</p></div>
        </div>
        <aside class="career-invite">
          <span class="eyebrow light">Komm ins Team</span>
          <h3>Deine Zukunft liegt oben.</h3>
          <p>Ob als Fachkraft oder zum Start ins Berufsleben: Bei uns arbeitest du mit moderner Ausstattung, erfahrenen Kollegen und sichtbaren Ergebnissen.</p>
          <div class="career-options"><div><strong>Fachkräfte</strong><span>Verantwortung übernehmen</span></div><div><strong>Ausbildung</strong><span>Handwerk von Grund auf lernen</span></div></div>
          <RouterLink to="/karriere" class="home-button accent">Jetzt bewerben <ArrowRight /></RouterLink>
        </aside>
      </div>
    </section>

    <section class="reviews-new">
      <div class="home-shell">
        <div class="section-intro centered reveal"><div><span class="eyebrow dark">Stimmen unserer Kunden</span><h2>Vertrauen entsteht durch Erfahrung.</h2></div></div>
        <div class="review-row">
          <article v-for="(review, index) in reviews" :key="review.name" class="review-new reveal" :style="{ '--delay': `${index * 100}ms` }">
            <Quote /><div class="review-stars"><Star v-for="n in 5" :key="n" /></div><p>{{ review.text }}</p><footer><strong>{{ review.name }}</strong><span>{{ review.place }}</span></footer>
          </article>
        </div>
      </div>
    </section>

    <section class="final-cta">
      <div class="home-shell cta-inner reveal">
        <Sparkles />
        <div><span class="eyebrow light">Ihr Projekt</span><h2>Was können wir für Ihr Dach tun?</h2></div>
        <RouterLink to="/kontakt" class="home-button accent">Unverbindlich anfragen <ArrowRight /></RouterLink>
      </div>
    </section>
  </main>
</template>
